// Registre des pages de route, pour les sortir du bundle initial.
//
// `App.tsx` importait les 16 pages en statique (contrainte du prérendu :
// `lazy()` ne se résout pas dans `renderToString`). Résultat : la home
// embarquait TermsPage, PrivacyPage, TarifsPage… ~225 Ko de source jamais
// exécutée, le premier poste de « JavaScript inutilisé » de l'audit.
//
// Trois consommateurs, trois besoins :
//  - prérendu (SSR)   : tout doit être résolu de façon SYNCHRONE → `warmupAllPages()`
//                       est attendu par entry-server avant le premier render ;
//  - hydratation      : SEULE la page de la route courante est pré-résolue
//                       (`warmupPageForPath`), pour qu'aucun `lazy` ne suspende
//                       et n'efface le HTML prérendu ;
//  - navigation client: tout le reste passe par `lazy()`, chargé à la demande.
import { lazy, type ComponentType } from 'react'
import { LOCALES, LOCALE_PREFIX } from '../i18n'
import { ROUTES, type StaticPageKey, localizedPath } from '../i18n/routes'

const PAGE_LOADERS = {
  about: () => import('./AboutPage'),
  contact: () => import('./ContactPage'),
  blog: () => import('./BlogPage'),
  sectors: () => import('./SectorsPage'),
  service: () => import('./ServicePage'),
  fulfillment: () => import('./FulfillmentPage'),
  pricing: () => import('./TarifsPage'),
  adr: () => import('./AdrPage'),
  afsca: () => import('./AfscaPage'),
  privacy: () => import('./PrivacyPage'),
  terms: () => import('./TermsPage'),
  legal: () => import('./LegalPage'),
  cookies: () => import('./CookiePage'),
  blogArticle: () => import('./BlogArticlePage'),
  sectorDetail: () => import('./SectorPage'),
  notFound: () => import('./NotFoundPage'),
} as const

export type PageModuleId = keyof typeof PAGE_LOADERS

/**
 * Chemin source de chaque page, tel qu'il apparaît dans le manifeste Vite.
 * Sert au prérendu à émettre le `modulepreload` du chunk de la route.
 * Un test vérifie que ces clés restent alignées sur PAGE_LOADERS.
 */
export const PAGE_SOURCES: Record<PageModuleId, string> = {
  about: 'src/pages/AboutPage.tsx',
  contact: 'src/pages/ContactPage.tsx',
  blog: 'src/pages/BlogPage.tsx',
  sectors: 'src/pages/SectorsPage.tsx',
  service: 'src/pages/ServicePage.tsx',
  fulfillment: 'src/pages/FulfillmentPage.tsx',
  pricing: 'src/pages/TarifsPage.tsx',
  adr: 'src/pages/AdrPage.tsx',
  afsca: 'src/pages/AfscaPage.tsx',
  privacy: 'src/pages/PrivacyPage.tsx',
  terms: 'src/pages/TermsPage.tsx',
  legal: 'src/pages/LegalPage.tsx',
  cookies: 'src/pages/CookiePage.tsx',
  blogArticle: 'src/pages/BlogArticlePage.tsx',
  sectorDetail: 'src/pages/SectorPage.tsx',
  notFound: 'src/pages/NotFoundPage.tsx',
}

/** Pages déjà résolues : lecture synchrone, aucun suspense. */
const resolved = new Map<PageModuleId, ComponentType>()
const lazies = new Map<PageModuleId, ComponentType>()

function lazyFor(id: PageModuleId): ComponentType {
  let c = lazies.get(id)
  if (!c) {
    c = lazy(PAGE_LOADERS[id]) as unknown as ComponentType
    lazies.set(id, c)
  }
  return c
}

/**
 * Emplacement de page. La résolution a lieu au RENDU, pas à l'évaluation du
 * module : c'est ce qui permet à `App.tsx` de construire sa table de routes au
 * scope module tout en laissant le SSR injecter ses composants avant de rendre.
 */
export function PageSlot({ id }: { id: PageModuleId }) {
  const Eager = resolved.get(id)
  const Cmp = Eager ?? lazyFor(id)
  return <Cmp />
}

async function resolvePage(id: PageModuleId) {
  if (resolved.has(id)) return
  resolved.set(id, (await PAGE_LOADERS[id]()).default as ComponentType)
}

/** SSR / prérendu : rend chaque page résolvable synchronement. */
export async function warmupAllPages() {
  await Promise.all((Object.keys(PAGE_LOADERS) as PageModuleId[]).map(resolvePage))
}

/** Toutes les pages sont-elles résolues ? (garde-fou du rendu serveur) */
export function areAllPagesReady() {
  return (Object.keys(PAGE_LOADERS) as PageModuleId[]).every((id) => resolved.has(id))
}

/** pageKey statique (i18n/routes) → module de page. */
const STATIC_KEY_TO_MODULE: Record<StaticPageKey, PageModuleId | null> = {
  home: null, // Landing vit dans App.tsx : c'est la page d'entrée principale.
  about: 'about',
  contact: 'contact',
  blog: 'blog',
  sectors: 'sectors',
  'service.storage': 'service',
  'service.pickpack': 'service',
  'service.shipping': 'service',
  'service.returns': 'service',
  fulfillment: 'fulfillment',
  pricing: 'pricing',
  'expertise.adr': 'adr',
  'expertise.afsca': 'afsca',
  privacy: 'privacy',
  terms: 'terms',
  legal: 'legal',
  cookies: 'cookies',
}

export const STATIC_PAGE_MODULES = STATIC_KEY_TO_MODULE

/** Module de page correspondant à une URL, ou null si la home. */
export function pageModuleForPath(pathname: string): PageModuleId | null {
  const path = pathname.replace(/\/+$/, '') || '/'
  for (const locale of LOCALES) {
    for (const key of Object.keys(STATIC_KEY_TO_MODULE) as StaticPageKey[]) {
      if (localizedPath(key, locale).replace(/\/+$/, '') === path.replace(/\/+$/, '')) {
        return STATIC_KEY_TO_MODULE[key]
      }
    }
    // Routes dynamiques `<segment>/:slug`
    const prefix = LOCALE_PREFIX[locale]
    if (new RegExp(`^${prefix}/${ROUTES.blog[locale]}/[^/]+$`).test(path)) return 'blogArticle'
    if (new RegExp(`^${prefix}/${ROUTES.sectors[locale]}/[^/]+$`).test(path)) return 'sectorDetail'
  }
  return 'notFound'
}

/**
 * Client : pré-résout la page de la route courante AVANT `hydrateRoot`, pour
 * que l'hydratation soit synchrone et ne remplace jamais le HTML prérendu.
 */
export async function warmupPageForPath(pathname: string) {
  const id = pageModuleForPath(pathname)
  if (id) await resolvePage(id)
}
